import { applicantSection, feeInfo, otherAttachments, replySection } from "modules/ebiForms/common";
import { householdFields, socialApplicantRoleSection, socialRoleAttachments } from "modules/ebiForms/definitions/sharedSocial";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * C1. Wniosek o przyznanie świadczenia z pomocy społecznej — art. 102 i art. 106 ust. 4 ustawy z 12.03.2004 r. o pomocy
 * społecznej; decyzja po rodzinnym wywiadzie środowiskowym (art. 107, rozporządzenie MRiPS z 8.04.2021 r., Dz.U. poz. 893).
 * Brak wzoru ogólnopolskiego; układ jak wzór MOPS Rzeszów (https://mopsrzeszow.pl/files/image/container/2022/WniosekPS.pdf,
 * pobrany 22.10.2026: „Zwracam się z prośbą o przyznanie mi pomocy w formie…”, „Prośbę swą uzasadniam następująco…”),
 * uzupełniony o skład gospodarstwa domowego i kto składa wniosek (art. 102) według „Uwag dla EBI” katalogu. Formy pomocy —
 * świadczenia z art. 36 ustawy. Dokumenty dochodowe (art. 107 ust. 5b) są nieobowiązkowe: pracownik socjalny żąda tych,
 * które odpowiadają sytuacji (większość można zastąpić oświadczeniem), a oświadczenie o stanie majątkowym składa się
 * w trakcie wywiadu — lista do potwierdzenia z ośrodkiem.
 */
const doc = (option: string): EbiCondition => ({ field: "dokumenty", includes: option });

const C1: IEbiFormDefinition = {
  code: "C1",
  version: 1,
  title: "Wniosek o przyznanie świadczenia z pomocy społecznej",
  category: EEbiFormCategory.Social,
  office: EEbiFormOffice.MunicipalUnit,
  description: "Zasiłek stały, okresowy lub celowy, posiłek, schronienie i inne formy pomocy ośrodka pomocy społecznej.",
  keywords: ["pomoc społeczna", "ops", "mops", "gops", "zasiłek celowy", "zasiłek okresowy", "zasiłek stały", "wywiad środowiskowy"],
  organ: "Wójt / Burmistrz / Prezydent Miasta — w praktyce kierownik ośrodka pomocy społecznej (centrum usług społecznych) z upoważnienia",
  legalBasis: "art. 102 i art. 106 ust. 4 ustawy z dnia 12 marca 2004 r. o pomocy społecznej",
  request: "Zwracam się z prośbą o przyznanie pomocy w formie wskazanej we wniosku.",
  feeLabel: "bez opłaty",
  fee: "Bez opłaty (art. 2 ust. 1 pkt 1 lit. c ustawy o opłacie skarbowej).",
  deadlineLabel: "do miesiąca",
  deadline:
    "Rodzinny wywiad środowiskowy — do 14 dni roboczych od złożenia wniosku (§ 3 ust. 2 rozporządzenia); decyzja po wywiadzie, do miesiąca (art. 35 KPA).",
  sections: [
    applicantSection(),
    socialApplicantRoleSection(),
    {
      code: "pomoc",
      title: "Rodzaj pomocy",
      fields: [
        feeInfo(
          "infoWywiad",
          "Decyzja zostanie wydana po rodzinnym wywiadzie środowiskowym, który pracownik socjalny przeprowadza w miejscu zamieszkania osoby zainteresowanej. Oświadczenie o stanie majątkowym składa się pracownikowi socjalnemu w trakcie wywiadu.",
        ),
        {
          code: "formyPomocy",
          type: "checkboxes",
          label: "Zwracam się z prośbą o przyznanie pomocy w formie",
          required: true,
          options: [
            { value: "zasilekStaly", label: "zasiłku stałego" },
            { value: "zasilekOkresowy", label: "zasiłku okresowego" },
            { value: "zasilekCelowy", label: "zasiłku celowego (np. na żywność, leki, opał, odzież)" },
            { value: "specjalnyZasilekCelowy", label: "specjalnego zasiłku celowego" },
            { value: "posilek", label: "posiłku" },
            { value: "schronienie", label: "schronienia" },
            { value: "ubranie", label: "niezbędnego ubrania" },
            { value: "pogrzeb", label: "sprawienia pogrzebu" },
            { value: "pracaSocjalna", label: "pracy socjalnej lub poradnictwa specjalistycznego" },
            { value: "inna", label: "innej" },
          ],
          hint: "Usługi opiekuńcze i skierowanie do domu pomocy społecznej mają w katalogu osobne wnioski.",
        },
        {
          code: "celZasilku",
          type: "text",
          label: "Na co ma być przeznaczony zasiłek celowy",
          required: true,
          visibleWhen: { any: [{ field: "formyPomocy", includes: "zasilekCelowy" }, { field: "formyPomocy", includes: "specjalnyZasilekCelowy" }] },
          maxLength: 300,
        },
        {
          code: "formaInna",
          type: "text",
          label: "Inna forma pomocy",
          required: true,
          visibleWhen: { field: "formyPomocy", includes: "inna" },
          maxLength: 300,
        },
        {
          code: "uzasadnienie",
          type: "textarea",
          label: "Prośbę uzasadniam następująco",
          required: true,
          maxLength: 5000,
          hint: "Opisz sytuację, która powoduje potrzebę pomocy (np. ubóstwo, bezrobocie, niepełnosprawność, długotrwała choroba, bezdomność).",
        },
      ],
    },
    {
      code: "gospodarstwoDomowe",
      title: "Gospodarstwo domowe",
      fields: householdFields(),
    },
    {
      code: "dokumentyDochodowe",
      title: "Dokumenty",
      description:
        "Dokumenty o dochodach nie są obowiązkowe przy składaniu wniosku — pracownik socjalny poprosi o te, które odpowiadają Twojej sytuacji. Jeśli je masz, możesz dołączyć je od razu.",
      fields: [
        {
          code: "dokumenty",
          type: "checkboxes",
          label: "Do wniosku dołączam",
          options: [
            { value: "wynagrodzenie", label: "zaświadczenie pracodawcy albo oświadczenie o wynagrodzeniu netto z miesiąca poprzedzającego" },
            { value: "emerytura", label: "decyzję lub odcinek emerytury, renty albo zasiłku (ZUS, KRUS)" },
            { value: "bezrobotny", label: "decyzję starosty o statusie bezrobotnego lub o zasiłku" },
            { value: "dzialalnosc", label: "zaświadczenie urzędu skarbowego albo oświadczenie o dochodzie z działalności gospodarczej" },
            { value: "rolne", label: "zaświadczenie o powierzchni gospodarstwa rolnego w ha przeliczeniowych lub o składce KRUS" },
            { value: "orzeczenie", label: "orzeczenie o niepełnosprawności lub o stopniu niepełnosprawności" },
            { value: "nauka", label: "zaświadczenie albo oświadczenie o kontynuowaniu nauki dziecka powyżej 16 lat" },
            { value: "pobyt", label: "kartę pobytu lub decyzję o statusie cudzoziemca" },
            { value: "zmianaDochodu", label: "dokument o utracie lub uzyskaniu dochodu (np. świadectwo pracy, umowa)" },
          ],
        },
      ],
    },
    replySection(),
  ],
  attachments: [
    ...socialRoleAttachments(),
    { code: "wynagrodzenie", label: "Zaświadczenie pracodawcy albo oświadczenie o wynagrodzeniu netto", visibleWhen: doc("wynagrodzenie"), required: true, multiple: true },
    { code: "emerytura", label: "Decyzja lub odcinek emerytury, renty albo zasiłku (ZUS, KRUS)", visibleWhen: doc("emerytura"), required: true, multiple: true },
    { code: "bezrobotny", label: "Decyzja starosty o statusie bezrobotnego lub o zasiłku", visibleWhen: doc("bezrobotny"), required: true },
    {
      code: "dzialalnosc",
      label: "Zaświadczenie urzędu skarbowego albo oświadczenie o dochodzie z działalności gospodarczej",
      hint: "Także dowód opłacania składek ZUS.",
      visibleWhen: doc("dzialalnosc"),
      required: true,
      multiple: true,
    },
    { code: "rolne", label: "Zaświadczenie o powierzchni gospodarstwa rolnego w ha przeliczeniowych lub o składce KRUS", visibleWhen: doc("rolne"), required: true, multiple: true },
    { code: "orzeczenie", label: "Orzeczenie o niepełnosprawności lub o stopniu niepełnosprawności", visibleWhen: doc("orzeczenie"), required: true, multiple: true },
    { code: "nauka", label: "Zaświadczenie albo oświadczenie o kontynuowaniu nauki", visibleWhen: doc("nauka"), required: true, multiple: true },
    { code: "pobyt", label: "Karta pobytu lub decyzja o statusie cudzoziemca", visibleWhen: doc("pobyt"), required: true },
    { code: "zmianaDochodu", label: "Dokument o utracie lub uzyskaniu dochodu", visibleWhen: doc("zmianaDochodu"), required: true, multiple: true },
    otherAttachments(),
  ],
};

export default C1;
